/**
 * BasicInfoDrawer — Edit server basic info (name, interface, area, status).
 *
 * Standard dp-drawer with 5 fields:
 *   - Description (text, required)
 *   - Interface (select: Hard-Wired / Firepanel / Inovonics)
 *   - Local IP Address (text, optional, IPv4 format enforced when present)
 *   - Area (select from areas list)
 *   - Active status (checkbox)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { isValidIpv4 } from '../../../../libs.jsx';

const INTERFACE_OPTIONS = [
  { id: 1, name: 'Hard-Wired' },
  { id: 2, name: 'Firepanel' },
  { id: 3, name: 'Inovonics' },
];

const BasicInfoDrawer = ({
  open, onClose, onSave, saving,
  serverName, interfaceId, areaId, isActive, areas, ipAddress,
}) => {
  const [description, setDescription] = useState('');
  const [selInterfaceId, setSelInterfaceId] = useState(1);
  const [selAreaId, setSelAreaId] = useState('');
  const [selIsActive, setSelIsActive] = useState(false);
  const [selIpAddress, setSelIpAddress] = useState('');
  const [errors, setErrors] = useState({});

  // Populate form from props only when drawer opens (false → true)
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setDescription(serverName || '');
      setSelInterfaceId(interfaceId || 1);
      setSelAreaId(areaId || '');
      setSelIsActive(isActive === 1);
      setSelIpAddress(ipAddress || '');
      setErrors({});
    }
    prevOpen.current = open;
  });

  const validate = useCallback(() => {
    const errs = {};
    if (!description.trim()) {
      errs.description = 'Description is required.';
    }
    // IP is optional — validate only when something was entered
    if (selIpAddress.trim() && !isValidIpv4(selIpAddress)) {
      errs.ipAddress = 'Enter a valid IP address (e.g. 192.168.1.10).';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [description, selIpAddress]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({
      description: description.trim(),
      interfaceId: selInterfaceId,
      areaId: selAreaId,
      isActive: selIsActive,
      ipAddress: selIpAddress.trim(),
    });
  }, [validate, onSave, description, selInterfaceId, selAreaId, selIsActive, selIpAddress]);

  // Sort areas by name
  const sortedAreas = [...(areas || [])].sort((a, b) =>
    (a.area_name || '').localeCompare(b.area_name || '')
  );

  return (
    <div className={`dp-drawer${open ? ' is-open' : ''}`}>
      <div className="dp-drawer__header">
        <h2 className="dp-drawer__title">Edit Server Info</h2>
        <button className="dp-drawer__close" onClick={onClose}>✕</button>
      </div>

      <form className="dp-drawer__body" onSubmit={handleSubmit}>
        {/* Description */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">
            Description <span className="dp-drawer__required">*</span>
          </label>
          <input
            type="text"
            className={`dp-drawer__input${errors.description ? ' is-invalid' : ''}`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Server name"
            autoFocus
          />
          {errors.description && <p className="dp-drawer__error">{errors.description}</p>}
        </div>

        {/* Interface */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">Interface</label>
          <select
            className="dp-drawer__select"
            value={selInterfaceId}
            onChange={(e) => setSelInterfaceId(parseInt(e.target.value, 10))}
          >
            {INTERFACE_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>{opt.name}</option>
            ))}
          </select>
        </div>

        {/* Local IP Address (optional) */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">Local IP Address</label>
          <input
            type="text"
            className={`dp-drawer__input${errors.ipAddress ? ' is-invalid' : ''}`}
            value={selIpAddress}
            onChange={(e) => setSelIpAddress(e.target.value)}
            placeholder="192.168.1.10 (optional)"
            inputMode="numeric"
          />
          {errors.ipAddress && <p className="dp-drawer__error">{errors.ipAddress}</p>}
        </div>

        {/* Area */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">Area</label>
          <select
            className="dp-drawer__select"
            value={selAreaId}
            onChange={(e) => setSelAreaId(e.target.value)}
          >
            <option value="">— None —</option>
            {sortedAreas.map((area) => (
              <option key={area.area_id} value={area.area_id}>{area.area_name}</option>
            ))}
          </select>
        </div>

        {/* Active Status */}
        <div className="dp-drawer__field dp-drawer__field--row">
          <input
            type="checkbox"
            className="dp-checkbox"
            id="server-active"
            checked={selIsActive}
            onChange={(e) => setSelIsActive(e.target.checked)}
          />
          <label htmlFor="server-active" className="dp-drawer__label dp-drawer__label--inline">
            Active
          </label>
        </div>

        {/* Footer */}
        <div className="dp-drawer__footer">
          <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="dp-btn dp-btn--primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BasicInfoDrawer;
